// Pick the per-session DATA-frame body cap based on what the selected
// ICE candidate pair tells us about the underlying network path.
//
// Two layers stack into the cap that matters:
//
//   * SCTP message size (~64 KiB): the browser's
//     `RTCDataChannel.send()` rejects messages above the negotiated
//     `maxMessageSize`. 16 KiB sits comfortably below every desktop
//     browser's floor.
//
//   * IP/MTU (~800 B): Tailscale's IPv6 path drops fragmented v6
//     packets by design, so on a ts-v6 ICE pair every wire-level
//     DataChannel message must fit in roughly one MTU-sized packet
//     after SCTP/DTLS overhead. ~800 B is the conservative figure
//     the box-side cap already uses for the same constraint
//     (see the spec's chunking notes).
//
// Default-conservative is the key. If we can't *positively confirm*
// the path is safe for 16 KiB (i.e. the selected pair resolves and
// its remote address is NOT in Tailscale's v6 ULA prefix), we cap
// at 800 — slower but correct. A detection miss fails to "slower
// but works," never to "wedge."

/** SCTP-only ceiling — used when the path is positively confirmed
 *  safe (direct, relay, or ts-v4). Matches the default cap
 *  `TranslatorClient` ships with. */
export const FRAME_BODY_CAP_CONFIRMED_SAFE = 16 * 1024;

/** Conservative IP/MTU ceiling — used on ts-v6 pairs OR whenever
 *  the selected pair can't be confirmed safe (stats unavailable,
 *  pair still gathering, unknown candidate type). */
export const FRAME_BODY_CAP_TS_V6_OR_UNKNOWN = 800;

/** Tailscale's IPv6 ULA prefix. Tailscale assigns every node a
 *  `fd7a:115c:a1e0:...` address; a candidate-pair whose remote is in
 *  this prefix indicates the data plane is traversing Tailscale's
 *  v6 transport, which can't carry IPv6 fragments. If Tailscale ever
 *  changes this prefix this constant needs updating; the cap-picker
 *  fails closed (returns the conservative cap), so the worst-case
 *  consequence of a missed prefix update is a perf regression on
 *  some confirmed-safe paths until we notice. */
const TAILSCALE_V6_PREFIX_LOWER = "fd7a:115c:a1e0:";

/** Subset of the RTCStats fields we need. Defined here so unit tests
 *  can feed a plain Map of fake stats without depending on the live
 *  RTCStatsReport type. */
export interface CandidatePairLike {
  type: "candidate-pair";
  state?: string;
  nominated?: boolean;
  selected?: boolean;
  remoteCandidateId?: string;
}
export interface RemoteCandidateLike {
  type: "remote-candidate";
  address?: string;
  ip?: string;
}
export type StatsLike =
  | RTCStatsReport
  | Map<string, CandidatePairLike | RemoteCandidateLike | { type: string }>;

/**
 * Inspect an RTCStatsReport-shaped map and return the per-session
 * DATA-frame body cap. See module header for the policy.
 *
 * Behavior under uncertainty:
 *   - No stats report  → ts-v6/unknown cap.
 *   - No selected pair → ts-v6/unknown cap.
 *   - Remote candidate not found / no address → ts-v6/unknown cap.
 *   - Remote address matches Tailscale's v6 ULA → ts-v6/unknown cap.
 *   - Anything else (including IPv4, non-Tailscale IPv6) → confirmed-safe cap.
 */
export function pickFrameBodyCapFromStats(stats: StatsLike | null): number {
  if (!stats) return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  // First pass: prefer a candidate-pair that's explicitly `selected`
  // (Chrome/Firefox). Second pass: any `succeeded` + `nominated`
  // pair (the closest cross-browser fallback when `selected` isn't
  // exposed).
  let pair: CandidatePairLike | null = null;
  for (const value of statsValues(stats)) {
    const v = value as Partial<CandidatePairLike> & { type?: string };
    if (v.type !== "candidate-pair") continue;
    const p = v as CandidatePairLike;
    if (p.selected === true) {
      pair = p;
      break;
    }
    if (p.state === "succeeded" && p.nominated === true && !pair) {
      pair = p;
    }
  }
  if (!pair) return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  const remoteId = pair.remoteCandidateId;
  if (!remoteId) return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  const remote = lookupStat(stats, remoteId) as
    | (Partial<RemoteCandidateLike> & { type?: string })
    | undefined;
  if (!remote || remote.type !== "remote-candidate") {
    return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  }
  const r = remote as RemoteCandidateLike;
  const addr = (r.address ?? r.ip ?? "").toLowerCase();
  if (!addr) return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  if (addr.startsWith(TAILSCALE_V6_PREFIX_LOWER)) {
    return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  }
  return FRAME_BODY_CAP_CONFIRMED_SAFE;
}

/**
 * Live-stats variant. Pulls `pc.getStats()` and feeds it through the
 * static picker. Any thrown error or rejected promise resolves to
 * the conservative cap — getStats failures must not wedge session
 * setup.
 *
 * Returns `number` synchronously when `pc.getStats` is missing or
 * throws synchronously (test fakes, very old browsers), so callers
 * don't pay an unnecessary microtask hop during session bring-up.
 */
export function pickFrameBodyCap(pc: {
  getStats?: () => Promise<RTCStatsReport>;
}): number | Promise<number> {
  let p: Promise<RTCStatsReport>;
  try {
    if (typeof pc.getStats !== "function") {
      return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
    }
    p = pc.getStats();
  } catch {
    return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  }
  if (!p || typeof (p as { then?: unknown }).then !== "function") {
    return FRAME_BODY_CAP_TS_V6_OR_UNKNOWN;
  }
  return p.then(pickFrameBodyCapFromStats, () => FRAME_BODY_CAP_TS_V6_OR_UNKNOWN);
}

// ---------- internal helpers ----------

function statsValues(stats: StatsLike): Iterable<unknown> {
  // `RTCStatsReport` is a Map; `forEach` works but `values()` is what
  // we want. Real reports also support iteration via Symbol.iterator.
  const m = stats as { values?: () => IterableIterator<unknown> };
  if (typeof m.values === "function") return m.values();
  // Shouldn't happen with a real RTCStatsReport, but fall back so
  // tests with unusual fakes don't crash.
  return [];
}

function lookupStat(stats: StatsLike, id: string): unknown {
  const m = stats as { get?: (k: string) => unknown };
  return typeof m.get === "function" ? m.get(id) : undefined;
}
